/**
 * Several tracks on one time axis.
 *
 * The interesting question is usually relative rather than absolute — did this
 * move when that did — and it is also the easiest chart to mislead with, since
 * putting two differently-scaled series in the same rectangle invites reading
 * their heights as comparable. Two decisions here push back on that:
 *
 *  - Normalisation is against each track's **declared** range, not its observed
 *    one. A track that only ever scored 5 to 7 out of 10 shows as a band across
 *    the middle. Stretching observed values to fill the axis would turn a
 *    stable fortnight into a dramatic sawtooth, which is a claim the data does
 *    not make.
 *  - The original value travels with every point, so a tooltip never has to
 *    show the normalised one. T-404 shows the real number; the 0–1 figure
 *    exists only to decide a height.
 *
 * No correlation, deliberately. See the ticket: a coefficient across
 * irregularly sampled self-reported series, printed in a document someone makes
 * decisions from, invites a conclusion the data cannot support.
 */

import type { Entry } from '../model/entry.js'
import type { Track } from '../model/track.js'
import type { EpochRange } from '../ports/storage.js'
import { buildSeries, bucketSeries } from './series.js'
import type { Aggregate, BucketSize } from './series.js'
import { describeQuantity, quantitativeRange } from './statistics.js'

export interface OverlayPoint {
  readonly bucketKey: string
  readonly startEpochMs: number
  /** 0–1 within the track's declared range. Height only, never a label. */
  readonly normalised: number
  /** The aggregate as recorded, in the track's own units. */
  readonly quantity: number
  /** That aggregate in the track's own words, for a tooltip. */
  readonly label: string
  readonly count: number
  readonly followsGap: boolean
}

export interface OverlaySeries {
  readonly track: Track
  readonly points: readonly OverlayPoint[]
  /** The declared bounds the normalisation was computed against. */
  readonly range: { readonly min: number; readonly max: number }
}

export type ExclusionReason = 'unordered' | 'noScale'

export interface ExcludedTrack {
  readonly track: Track
  readonly reason: ExclusionReason
}

/** Says why a track cannot be overlaid, in words the UI shows verbatim. */
export function describeExclusion(excluded: ExcludedTrack): string {
  switch (excluded.reason) {
    case 'unordered':
      return `${excluded.track.name} has options with no order, so there is no line to draw — only counts.`
    case 'noScale':
      return `${excluded.track.name} has no numeric scale to compare against.`
  }
}

export interface AxisSlot {
  readonly key: string
  readonly startEpochMs: number
}

export interface Overlay {
  readonly size: BucketSize
  readonly aggregate: Aggregate
  readonly series: readonly OverlaySeries[]
  readonly excluded: readonly ExcludedTrack[]
  /** Every period any track has data in, ascending. The shared x-axis. */
  readonly axis: readonly AxisSlot[]
}

export interface OverlayInput {
  readonly track: Track
  readonly entries: readonly Entry[]
}

export interface OverlayOptions {
  readonly size: BucketSize
  readonly aggregate?: Aggregate
  readonly range?: EpochRange
}

/**
 * Builds the aligned, normalised series an overlay chart draws.
 *
 * Alignment is by bucket key, so tracks measured at different times of day
 * still share a column. Gaps stay per-track: one track logged every day and
 * another logged twice a week produce different breaks on the same axis, which
 * is the truth of what was recorded.
 */
export function buildOverlay(
  inputs: readonly OverlayInput[],
  options: OverlayOptions,
): Overlay {
  const aggregate = options.aggregate ?? 'mean'
  const series: OverlaySeries[] = []
  const excluded: ExcludedTrack[] = []
  const axis = new Map<string, number>()

  for (const input of inputs) {
    const bounds = quantitativeRange(input.track)
    if (bounds === null) {
      excluded.push({
        track: input.track,
        reason: input.track.kind.kind === 'enum' ? 'unordered' : 'noScale',
      })
      continue
    }

    const built = buildSeries(input.track, input.entries, options.range)
    if (built.kind !== 'quantitative') {
      excluded.push({ track: input.track, reason: 'unordered' })
      continue
    }

    const bucketed = bucketSeries(built, { size: options.size, aggregate })
    if (bucketed.kind !== 'quantitative') {
      continue
    }

    // A zero-width range would divide by zero. It cannot happen through the
    // track editor, which requires min < max, but a single-option ordered enum
    // could arrive from an import.
    const span = bounds.max - bounds.min
    const points = bucketed.buckets.map((bucket) => {
      axis.set(bucket.bucket.key, bucket.bucket.startEpochMs)
      return {
        bucketKey: bucket.bucket.key,
        startEpochMs: bucket.bucket.startEpochMs,
        normalised: span === 0 ? 0.5 : (bucket.value - bounds.min) / span,
        quantity: bucket.value,
        label: describeQuantity(input.track, bucket.value),
        count: bucket.count,
        followsGap: bucket.followsGap,
      }
    })

    series.push({ track: input.track, points, range: bounds })
  }

  return {
    size: options.size,
    aggregate,
    series,
    excluded,
    axis: [...axis.entries()]
      .map(([key, startEpochMs]) => ({ key, startEpochMs }))
      .sort((a, b) => a.startEpochMs - b.startEpochMs),
  }
}
